/**
 * Drop the columns an UPDATE has already assigned from the candidate list.
 *
 * `UPDATE orders SET status = 'x', |` must not re-offer `status` on the engines
 * that reject a repeated assignment target. Counterpart of the INSERT
 * column-list filter, and shared by all five resolvers because the
 * assignment-list grammar is uniform.
 *
 * SQLite and MySQL are deliberately excluded: both ACCEPT a duplicate target
 * and apply the last assignment (verified on SQLite 3 —
 * `UPDATE t SET a = 2, a = 3` leaves `a` at 3), so removing the column there
 * would hide a legal completion. Postgres ("multiple assignments to same
 * column"), SQL Server and Oracle all raise an error, so on those the
 * already-assigned names are illegal at the next slot.
 */

import type { CompletionCandidate, DatabaseEngineId } from '@/core/engines';
import { findAssignedSetColumns } from '../context/findTypedInsertColumns';

/** The engines that reject a repeated SET target. */
const REJECTS_DUPLICATE_SET_TARGET: ReadonlySet<DatabaseEngineId> = new Set([
  'pgsql',
  'mssql',
  'oracle',
]);

export function filterAlreadyAssignedSetColumns(
  out: CompletionCandidate[],
  text: string,
  offset: number,
  statementStart: number,
  statementEnd: number,
  engine: DatabaseEngineId,
): void {
  if (!REJECTS_DUPLICATE_SET_TARGET.has(engine)) return;
  const assigned = findAssignedSetColumns(text, offset, statementStart, statementEnd, engine);
  if (!assigned || assigned.length === 0) return;
  const taken = new Set(assigned);
  for (let i = out.length - 1; i >= 0; i -= 1) {
    const c = out[i];
    if (c.kind !== 'column') continue;
    if (taken.has(c.label.toLowerCase())) out.splice(i, 1);
  }
}
